/**
 * Carrossel de cards — CSS e HTML compartilhados entre visualização e exportação.
 * O palco espelha as mesmas classes em RenderCardSlider.
 */

export const CARD_SLIDER_MAX_PER_PAGE = 24;

function clampInt(value, min, max, fallback) {
  const n = parseInt(value, 10);
  if (Number.isNaN(n)) return fallback;
  return Math.max(min, Math.min(max, n));
}

/** Tamanho próprio do texto. Se ainda não foi separado, acompanha o tamanho único antigo. */
export function resolveScaledFontPx(content, key, fallback, ratio = 1) {
  const direct = parseInt(content?.[key], 10);
  if (Number.isFinite(direct) && direct > 0) return clampInt(direct, 10, 72, fallback);
  const base = parseInt(content?.wrapperFont, 10);
  if (Number.isFinite(base) && base > 0) return clampInt(Math.round(base * ratio), 10, 72, fallback);
  return fallback;
}

function escText(value) {
  return String(value == null ? '' : value)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');
}

function escAttr(value) {
  return escText(value).replace(/"/g, '&quot;');
}

function isRootRelative(link) {
  const raw = String(link || '').trim();
  if (!raw || raw === '#') return false;
  if (/^[a-zA-Z][a-zA-Z0-9+.-]*:/.test(raw) || raw.slice(0, 2) === '//') return false;
  return raw.charAt(0) === '/';
}

export function cardSliderPageStarts(count, per, centered) {
  const total = Math.max(0, count);
  const size = Math.max(1, per);
  if (total <= 0) return [0];
  if (centered) {
    const pages = [];
    for (let start = 0; start < total; start += size) pages.push(start);
    return pages.length ? pages : [0];
  }
  if (total <= size) return [0];
  const starts = [];
  for (let start = 0; start <= total - size; start += size) starts.push(start);
  const last = total - size;
  if (starts[starts.length - 1] !== last) starts.push(last);
  return starts;
}

export function cardSliderMetrics(content, viewportWidth, page, mobile) {
  const cards = Array.isArray(content?.cards) ? content.cards : [];
  const count = cards.length;
  const layoutName = content?.layout;
  const centered = layoutName === 'centered' || layoutName === 'youtube';
  const rawPer = mobile ? content?.perPageMobile : content?.perPage;
  let per = clampInt(rawPer, 1, CARD_SLIDER_MAX_PER_PAGE, mobile ? 1 : 3);
  const gap = clampInt(content?.gap, 0, 48, 16);
  const width = Math.max(0, Number(viewportWidth) || 0);
  const configuredPeek = clampInt(content?.peek, 12, 180, 56);
  const edge = !centered && width > 0 && count > per
    ? Math.max(24, Math.min(configuredPeek, Math.round(width * 0.18)))
    : 0;
  const slots = Math.max(1, per);
  const rawCard = width > 0 ? (width - edge - gap * Math.max(0, slots - 1)) / slots : 0;
  const cardW = width > 0 ? Math.max(1, centered ? Math.floor(rawCard) : rawCard) : 0;
  const starts = cardSliderPageStarts(count, per, centered);
  const pages = Math.max(1, starts.length);
  const raw = Number(page) || 0;
  const safePage = content?.loop === false
    ? Math.max(0, Math.min(pages - 1, raw))
    : ((raw % pages) + pages) % pages;
  const start = starts[safePage] || 0;
  const x = -start * (cardW + gap);
  return { cardW, gap, x, starts, page: safePage, per, peek: edge, centered, count };
}

export const CARD_SLIDER_CSS = `
.madri-card-slider{position:relative;box-sizing:border-box;padding:8px 0 4px;max-width:100%}
.madri-card-slider__viewport{overflow:hidden !important;width:100%}
.madri-card-slider__track{display:flex !important;flex-wrap:nowrap !important;align-items:stretch;transition:transform .45s ease;will-change:transform}
.madri-card-slider__card{box-sizing:border-box;display:flex !important;flex-direction:column;min-width:0;max-width:none !important;background:var(--cs-card,#fff);border-radius:var(--cs-radius,14px);overflow:hidden;text-decoration:none !important;color:inherit}
.madri-card-slider--shadow .madri-card-slider__card{box-shadow:0 10px 24px rgba(15,23,42,.12)}
.madri-card-slider--lift .madri-card-slider__card{transition:transform .2s ease,box-shadow .2s ease}
.madri-card-slider--lift .madri-card-slider__card:hover{transform:translateY(-6px)}
.madri-card-slider__media,.madri-moodle-layout .madri-card-slider__media{display:block !important;width:100% !important;max-width:none !important;height:var(--cs-img,160px) !important;object-fit:cover !important;background:#e2e8f0;border-radius:0 !important}
.madri-card-slider__ph{height:var(--cs-img,160px);background:linear-gradient(160deg,#e2e8f0,#f8fafc);flex:0 0 auto}
.madri-card-slider__body{display:flex;flex-direction:column;gap:8px;padding:14px 16px 16px;flex:1}
.madri-card-slider--center-text .madri-card-slider__body{align-items:center;text-align:center}
.madri-card-slider__title,.madri-moodle-layout .madri-card-slider__title{margin:0;color:var(--cs-title,#0f172a) !important;font-size:var(--cs-title-size,16px);font-weight:700;line-height:inherit}
.madri-card-slider__text,.madri-moodle-layout .madri-card-slider__text{margin:0;color:var(--cs-text,#475569) !important;font-size:var(--cs-text-size,14px);line-height:inherit}
.madri-card-slider__btn,.madri-moodle-layout .madri-card-slider__btn{display:inline-flex;align-items:center;justify-content:center;margin-top:auto;padding:8px 14px;border-radius:8px;background:var(--cs-btn,#2563eb) !important;color:var(--cs-btn-text,#fff) !important;text-decoration:none !important;font-size:0.8125em;font-weight:700;line-height:1}
.madri-card-slider__arrow{position:absolute;top:calc(var(--cs-img,160px) / 2);transform:translateY(-50%);z-index:2;width:36px;height:36px;border:0;border-radius:999px;background:var(--cs-arrow,#fff);color:var(--cs-arrow-text,#0f172a);box-shadow:0 4px 14px rgba(15,23,42,.18);cursor:pointer;font-size:22px;line-height:1;display:flex;align-items:center;justify-content:center}
.madri-card-slider__arrow:disabled{opacity:.35;cursor:default}
.madri-card-slider__arrow--prev{left:4px}
.madri-card-slider__arrow--next{right:4px}
.madri-card-slider__dots{display:flex;justify-content:center;gap:6px;margin-top:12px}
.madri-card-slider__dot{width:8px;height:8px;padding:0;border:0;border-radius:999px;background:var(--cs-dot,#cbd5e1);cursor:pointer}
.madri-card-slider__dot.is-active{width:18px;background:var(--cs-dot-on,#2563eb)}
@media (prefers-reduced-motion:reduce){.madri-card-slider__track,.madri-card-slider--lift .madri-card-slider__card{transition:none}}
`.replace(/\s+/g, ' ').trim();

export const CARD_SLIDER_BOOT = `<script>
(function(){
  function pageStarts(count, per, centered) {
    if (count <= 0) return [0];
    if (centered) {
      var pages = [];
      for (var s = 0; s < count; s += per) pages.push(s);
      return pages.length ? pages : [0];
    }
    if (count <= per) return [0];
    var starts = [];
    for (var s = 0; s <= count - per; s += per) starts.push(s);
    var last = count - per;
    if (starts[starts.length - 1] !== last) starts.push(last);
    return starts;
  }
  function boot(root) {
    if (!root || root.getAttribute('data-cs-ready') === '1') return;
    root.setAttribute('data-cs-ready', '1');
    var viewport = root.querySelector('[data-cs="viewport"]') || root.querySelector('.madri-card-slider__viewport');
    var track = root.querySelector('[data-cs="track"]') || root.querySelector('.madri-card-slider__track');
    var cards = track ? track.children : [];
    var prev = root.querySelector('[data-cs="prev"]');
    var next = root.querySelector('[data-cs="next"]');
    var dots = root.querySelector('[data-cs="dots"]') || root.querySelector('.madri-card-slider__dots');
    var dotClass = root.getAttribute('data-cs-dot') || 'madri-card-slider__dot';
    var page = 0;
    var timer = null;
    function num(name, fallback) {
      var n = parseInt(root.getAttribute(name), 10);
      return isNaN(n) ? fallback : n;
    }
    function metrics() {
      var count = cards.length;
      var layoutName = root.getAttribute('data-layout');
      var centered = layoutName === 'centered' || layoutName === 'youtube';
      var per = window.matchMedia('(max-width:767px)').matches ? num('data-per-mobile', 1) : num('data-per', 3);
      per = Math.max(1, Math.min(${CARD_SLIDER_MAX_PER_PAGE}, per));
      var gap = Math.max(0, Math.min(48, num('data-gap', 16)));
      var peek = Math.max(12, Math.min(180, num('data-peek', 56)));
      var width = viewport ? viewport.clientWidth : 0;
      var edge = (!centered && count > per) ? Math.max(24, Math.min(peek, Math.round(width * 0.18))) : 0;
      var rawCard = (width - edge - gap * Math.max(0, per - 1)) / per;
      var cardW = centered ? Math.max(1, Math.floor(rawCard)) : Math.max(1, rawCard);
      var starts = pageStarts(count, per, centered);
      if (page >= starts.length) page = 0;
      var start = starts[page] || 0;
      var x = -start * (cardW + gap);
      return { cardW: cardW, gap: gap, x: x, starts: starts };
    }
    function paint() {
      var m = metrics();
      for (var i = 0; i < cards.length; i++) {
        cards[i].style.flex = '0 0 ' + m.cardW + 'px';
        cards[i].style.width = m.cardW + 'px';
      }
      if (track) {
        track.style.gap = m.gap + 'px';
        track.style.transform = 'translateX(' + m.x + 'px)';
      }
      if (dots) {
        if (dots.children.length !== m.starts.length) {
          var html = '';
          for (var d = 0; d < m.starts.length; d++) html += '<button type="button" data-cs-page="' + d + '" class="' + dotClass + '" aria-label="' + (d + 1) + '"></button>';
          dots.innerHTML = html;
        }
        var buttons = dots.querySelectorAll('button');
        for (var b = 0; b < buttons.length; b++) {
          if (b === page) buttons[b].classList.add('is-active');
          else buttons[b].classList.remove('is-active');
        }
      }
      var loop = root.getAttribute('data-loop') === '1';
      if (prev) prev.disabled = !loop && page <= 0;
      if (next) next.disabled = !loop && page >= m.starts.length - 1;
    }
    function go(delta) {
      var m = metrics();
      var total = m.starts.length;
      if (total < 2) return;
      var loop = root.getAttribute('data-loop') === '1';
      var nextPage = page + delta;
      if (loop) nextPage = (nextPage + total) % total;
      else nextPage = Math.max(0, Math.min(total - 1, nextPage));
      page = nextPage;
      paint();
    }
    if (prev) prev.addEventListener('click', function(ev){ ev.preventDefault(); go(-1); });
    if (next) next.addEventListener('click', function(ev){ ev.preventDefault(); go(1); });
    if (dots) dots.addEventListener('click', function(ev){
      var btn = ev.target.closest ? ev.target.closest('[data-cs-page]') : null;
      if (!btn) return;
      page = parseInt(btn.getAttribute('data-cs-page'), 10) || 0;
      paint();
    });
    var startX = 0;
    var dragging = false;
    if (viewport) {
      viewport.addEventListener('pointerdown', function(ev){
        if (ev.target.closest && ev.target.closest('a,button')) return;
        dragging = true;
        startX = ev.clientX;
      });
      window.addEventListener('pointerup', function(ev){
        if (!dragging) return;
        dragging = false;
        var dx = ev.clientX - startX;
        if (dx > 48) go(-1);
        else if (dx < -48) go(1);
      });
    }
    root.addEventListener('keydown', function(ev){
      if (ev.key === 'ArrowLeft') { ev.preventDefault(); go(-1); }
      if (ev.key === 'ArrowRight') { ev.preventDefault(); go(1); }
    });
    function arm() {
      if (timer) clearInterval(timer);
      timer = null;
      var ms = num('data-autoplay', 0);
      if (ms < 1200) return;
      timer = setInterval(function(){ go(1); }, ms);
    }
    root.addEventListener('mouseenter', function(){ if (timer) { clearInterval(timer); timer = null; } });
    root.addEventListener('mouseleave', function(){ if (root.getAttribute('data-vs-hold') === '1') return; arm(); });
    window.addEventListener('resize', paint);
    paint();
    arm();
  }
  window.madriCardSliderBoot = boot;
})();
</script>`;

function linkAttrs(href, openNewTab) {
  const url = String(href || '').trim();
  if (!url) return '';
  if (isRootRelative(url)) {
    return ` href="#" data-madri-href="${escAttr(url)}" onclick="event.preventDefault();var u=this.getAttribute('data-madri-href');if(u){if(typeof madriGoRedirect==='function'){madriGoRedirect(u);}else{try{(window.top||window).location.href=u;}catch(e){location.href=u;}}}"`;
  }
  const target = openNewTab && url.charAt(0) !== '#' ? ' target="_blank" rel="noopener noreferrer"' : '';
  return ` href="${escAttr(url)}"${target}`;
}

function cardInner(card) {
  const image = String(card?.imageUrl || '').trim();
  const title = String(card?.title || '').trim();
  const text = String(card?.text || '').trim();
  const button = String(card?.buttonLabel || '').trim();
  const href = String(card?.href || '').trim();
  const imageHtml = image
    ? `<img class="madri-card-slider__media" src="${escAttr(image)}" alt="${escAttr(title || '')}">`
    : '<div class="madri-card-slider__ph"></div>';
  const buttonHtml = button
    ? `<a class="madri-card-slider__btn"${href ? linkAttrs(href, card?.openNewTab) : ' href="#" onclick="event.preventDefault()"'}>${escText(button)}</a>`
    : '';
  const body = (title || text || button)
    ? `<div class="madri-card-slider__body">${title ? `<div class="madri-card-slider__title">${escText(title)}</div>` : ''}${text ? `<div class="madri-card-slider__text">${escText(text)}</div>` : ''}${buttonHtml}</div>`
    : '';
  const inner = `${imageHtml}${body}`;
  if (!button && href) {
    return `<a class="madri-card-slider__card"${linkAttrs(href, card?.openNewTab)}>${inner}</a>`;
  }
  return `<div class="madri-card-slider__card">${inner}</div>`;
}

export function createDefaultCardSliderContent(labels = {}) {
  const card = (title) => ({
    imageUrl: '',
    title,
    text: labels.cardText || 'Um texto curto sobre este card.',
    buttonLabel: labels.button || 'Saiba mais',
    href: '#',
    openNewTab: false,
  });
  return {
    layout: 'netflix',
    cardAlign: 'left',
    perPage: 3,
    perPageMobile: 1,
    gap: 16,
    radius: 14,
    imageHeight: 160,
    peek: 56,
    autoplay: false,
    autoplayMs: 4000,
    loop: true,
    showArrows: true,
    showDots: true,
    hoverLift: true,
    shadow: true,
    cardBg: '#ffffff',
    titleColor: '#0f172a',
    textColor: '#475569',
    btnBg: '#2563eb',
    btnColor: '#ffffff',
    arrowBg: '#ffffff',
    arrowColor: '#0f172a',
    dotColor: '#cbd5e1',
    dotActive: '#2563eb',
    cards: [
      card(labels.card1 || 'Card 1'),
      card(labels.card2 || 'Card 2'),
      card(labels.card3 || 'Card 3'),
      card(labels.card4 || 'Card 4'),
    ],
  };
}

export function buildCardSliderInnerHtml(content, widgetId) {
  const c = content || {};
  const cards = Array.isArray(c.cards) ? c.cards.slice(0, 12) : [];
  const layout = c.layout === 'centered' ? 'centered' : 'netflix';
  const id = `cs_${String(widgetId || 'slider').replace(/[^a-zA-Z0-9_-]/g, '')}`;
  const vars = [
    `--cs-card:${c.cardBg || '#ffffff'}`,
    `--cs-title:${c.titleColor || '#0f172a'}`,
    `--cs-text:${c.textColor || '#475569'}`,
    `--cs-title-size:${resolveScaledFontPx(c, 'cardTitleFontPx', 16, 1)}px`,
    `--cs-text-size:${resolveScaledFontPx(c, 'cardTextFontPx', 14, 0.875)}px`,
    `--cs-btn:${c.btnBg || '#2563eb'}`,
    `--cs-btn-text:${c.btnColor || '#ffffff'}`,
    `--cs-arrow:${c.arrowBg || '#ffffff'}`,
    `--cs-arrow-text:${c.arrowColor || '#0f172a'}`,
    `--cs-dot:${c.dotColor || '#cbd5e1'}`,
    `--cs-dot-on:${c.dotActive || '#2563eb'}`,
    `--cs-radius:${clampInt(c.radius, 0, 32, 14)}px`,
    `--cs-img:${clampInt(c.imageHeight, 80, 420, 160)}px`,
  ].join(';');
  const classes = [
    'madri-card-slider',
    layout === 'centered' ? 'madri-card-slider--centered' : 'madri-card-slider--netflix',
    c.cardAlign === 'center' ? 'madri-card-slider--center-text' : '',
    c.shadow === false ? '' : 'madri-card-slider--shadow',
    c.hoverLift === false ? '' : 'madri-card-slider--lift',
  ].filter(Boolean).join(' ');
  const arrows = c.showArrows === false ? '' : `<button type="button" class="madri-card-slider__arrow madri-card-slider__arrow--prev" data-cs="prev" aria-label="Anterior">‹</button><button type="button" class="madri-card-slider__arrow madri-card-slider__arrow--next" data-cs="next" aria-label="Próximo">›</button>`;
  const dots = c.showDots === false ? '' : '<div class="madri-card-slider__dots"></div>';
  const autoplay = c.autoplay ? clampInt(c.autoplayMs, 1200, 20000, 4000) : 0;
  return `<style>${CARD_SLIDER_CSS}</style>${CARD_SLIDER_BOOT}<div id="${id}" class="${classes}" style="${escAttr(vars)}" data-madri-card-slider="1" data-layout="${layout}" data-per="${clampInt(c.perPage, 1, CARD_SLIDER_MAX_PER_PAGE, 3)}" data-per-mobile="${clampInt(c.perPageMobile, 1, CARD_SLIDER_MAX_PER_PAGE, 1)}" data-gap="${clampInt(c.gap, 0, 48, 16)}" data-peek="${clampInt(c.peek, 12, 180, 56)}" data-loop="${c.loop === false ? '0' : '1'}" data-autoplay="${autoplay}" tabindex="0"><div class="madri-card-slider__viewport"><div class="madri-card-slider__track">${cards.map((card) => cardInner(card)).join('')}</div></div>${arrows}${dots}</div><script>if(window.madriCardSliderBoot){window.madriCardSliderBoot(document.getElementById('${id}'));}</script>`;
}
